// ts-rest contract for Comply > Consumer Duty.
// Composes ConsumerDutyPillar (projected to the legacy `assessments` view
// shape) + ConsumerDutyClientScore + the vulnerable_customers reference table
// behind one typed read. Per ADR-0003 the rows the server knows the shape of
// are real `z.object` schemas, not `z.record(z.unknown())` stand-ins.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const ragStatusSchema = z.enum(['Red', 'Amber', 'Green']);

// Projected pillar — `toAssessmentView` (consumer-duty-display.ts) maps the
// ConsumerDutyPillar Object Type onto the legacy assessments view shape the
// page renders: the PILLAR_DISPLAY name and the `lastReviewDate` /
// `nextReviewDate` field names.
export const consumerDutyAssessmentDtoSchema = z.object({
    id: z.string(),
    pillar: z.string(),
    overallScore: z.number(),
    ragStatus: ragStatusSchema,
    findingsCount: z.number(),
    keyFindings: z.array(z.string()),
    actionsRequired: z.number(),
    lastReviewDate: z.string().nullable(),
    nextReviewDate: z.string(),
});
export type ConsumerDutyAssessmentDto = z.infer<typeof consumerDutyAssessmentDtoSchema>;

// The ConsumerDutyClientScore Object Type row (InferObject), projected
// field-by-field. The score has no `clientName` ontology property (the
// Client link is the canonical relationship); the page tolerates the absent
// name. `acknowledged` is derived from `acknowledgedAt != null`.
export const consumerDutyClientScoreDtoSchema = z.object({
    id: z.string(),
    productsScore: z.number(),
    priceValueScore: z.number(),
    understandingScore: z.number(),
    supportScore: z.number(),
    overallScore: z.number(),
    ragStatus: ragStatusSchema,
    flags: z.array(z.string()),
    acknowledged: z.boolean(),
});
export type ConsumerDutyClientScoreDto = z.infer<typeof consumerDutyClientScoreDtoSchema>;

// The vulnerable_customers reference table row (a direct Drizzle select, not
// an Object Type). `drivers` is a free-form jsonb string array on the source
// row; typed as a string array (the page renders each as a driver pill).
export const vulnerableCustomerDtoSchema = z.object({
    id: z.string(),
    clientName: z.string().nullable(),
    drivers: z.array(z.string()),
    detail: z.string().nullable(),
    supportPlan: z.string().nullable(),
    dateIdentified: z.string().nullable(),
    reviewDate: z.string().nullable(),
    assignedTo: z.string().nullable(),
    status: z.string().nullable(),
});
export type VulnerableCustomerDto = z.infer<typeof vulnerableCustomerDtoSchema>;

export const consumerDutyResponseSchema = z.object({
    assessments: z.array(consumerDutyAssessmentDtoSchema),
    clientScores: z.array(consumerDutyClientScoreDtoSchema),
    vulnerableCustomers: z.array(vulnerableCustomerDtoSchema),
});
export type ConsumerDutyResponse = z.infer<typeof consumerDutyResponseSchema>;

import { errorBody } from '../schemas/common';
const okBody = z.object({ ok: z.literal(true) });

export const complyConsumerDutyContract = c.router(
    {
        get: {
            method: 'GET',
            path: '/comply/consumer-duty',
            responses: { 200: consumerDutyResponseSchema, 403: errorBody },
            summary: 'Composed Comply > Consumer Duty payload',
        },
        triggerRescore: {
            method: 'POST',
            path: '/comply/consumer-duty/rescore',
            // Queues a consumer_duty_rescore agent task. `scoreId` anchors a
            // per-client rescore; `clientId` / `clientName` carry the trigger
            // detail (firm-wide when omitted).
            body: z.object({
                scoreId: z.string(),
                clientId: z.string().optional(),
                clientName: z.string().optional(),
            }),
            responses: { 200: okBody, 400: errorBody, 403: errorBody },
            summary: 'Queue a Consumer Duty rescore agent task',
        },
        acknowledge: {
            method: 'POST',
            path: '/comply/consumer-duty/:scoreId/acknowledge',
            pathParams: z.object({ scoreId: z.string() }),
            body: z.object({}).optional(),
            responses: { 200: okBody, 400: errorBody, 403: errorBody },
            summary: 'Acknowledge a Consumer Duty client-score flag',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
